import { useState } from "react";
import { useAuth } from "../lib/auth";
import { fmtMoney } from "./format";
import { getDraft, setDraft, clearDraft } from "./drafts";

// The single "what is yours to decide" queue (M6 items 2+3). Amber throughout — the semantic rule is
// amber = "needs a human", and this is the one region that always does. A gap and a conflict read
// differently on purpose: a gap asks for something missing; a conflict says the Underwriter found the
// problem in the owner's OWN paperwork and would not paper over it. No number is ever tinted here.

function AddressMismatchCard({ item, buildings, onAddBuilding, submit, busy }) {
  const [picking, setPicking] = useState(false);
  const others = (buildings || []).filter((b) => b.building_ref !== item.building_ref);
  return (
    <div className="decision-choices" data-testid="decision-address-choices">
      {!picking ? (
        <div className="decision-choice-row">
          {item.options.map((o, i) => (
            <button key={i} className="decision-choice" disabled={busy}
                    data-testid={"decision-address-" + o.value}
                    onClick={() => (o.value === "different_building"
                      ? setPicking(true)
                      : submit(o.value))}>
              {o.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="decision-address-move" data-testid="decision-address-move">
          <p className="decision-sub">
            Tell me which one — I won't move a document on my own, and I won't guess.
          </p>
          <div className="decision-choice-row">
            {others.map((b) => (
              <button key={b.building_ref} className="decision-choice" disabled={busy}
                      data-testid={"decision-address-target-" + b.building_ref}
                      onClick={() => submit({ choice: "different_building", target: b.building_ref })}>
                {b.label}
              </button>
            ))}
            {others.length === 0 && (
              <span className="decision-sub" data-testid="decision-address-no-others">
                You have no other building on the desk yet.
              </span>
            )}
            <button className="decision-choice" disabled={busy}
                    data-testid="decision-address-add-building"
                    onClick={() => onAddBuilding && onAddBuilding()}>Add that building</button>
            <button className="decision-choice" disabled={busy}
                    data-testid="decision-address-back" onClick={() => setPicking(false)}>Back</button>
          </div>
        </div>
      )}
    </div>
  );
}

function DecisionCard({ item, onDone, buildings, onAddBuilding }) {
  const { authedFetch, session } = useAuth();
  const acct = session?.account_id;
  const draftKey = "decision:" + item.id;
  // Restore any in-memory draft for THIS account so a session-end during a submit never loses the
  // owner's typed text — and never surfaces one account's text to another.
  const [value, setValueState] = useState(() => getDraft(acct, draftKey));
  const setValue = (v) => { setValueState(v); setDraft(acct, draftKey, v); };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (v) => {
    if (busy || (v === undefined && !value.trim())) return;
    setBusy(true); setErr("");
    try {
      await authedFetch("/owner/commands/decision/" + item.id, {
        method: "POST", body: { value: v !== undefined ? v : value.trim() },
      });
      clearDraft(acct, draftKey);
      onDone();
    } catch (e) {
      setErr((typeof e === "string" ? e : e && (e.detail || e.message))
             || "That didn't go through. Try again.");
      setBusy(false);
    }
  };
  const isAddress = item.subkind === "address_mismatch";
  return (
    <div className={"decision-card decision-" + item.kind} data-testid={"decision-" + item.id}>
      <div className="decision-top">
        <span className="decision-kind" data-testid="decision-kind">
          {isAddress ? "Which building is this document for?"
            : item.kind === "conflict" ? "Your documents disagree" : "I could not find this"}
        </span>
        <span className="decision-building">{item.building_label}</span>
      </div>
      <p className="decision-prompt">{item.prompt}</p>
      {isAddress ? (
        <AddressMismatchCard item={item} buildings={buildings} onAddBuilding={onAddBuilding}
                             submit={submit} busy={busy} />
      ) : item.kind === "conflict" ? (
        <div className="decision-options">
          {item.options.map((o, i) => (
            <button key={i} className="decision-option" disabled={busy}
                    data-testid="decision-option" onClick={() => submit(o.value)}>
              <span className="decision-option-val">{fmtMoney(o.value)}</span>
              {o.as_of && <span className="decision-option-asof">as of {o.as_of}</span>}
            </button>
          ))}
        </div>
      ) : item.options && item.options.length > 0 ? (
        // A gap the join can offer candidates for (e.g. a blank submarket) — suggestions, never a
        // pre-selection (founder ruling 20 Sep §2). A free-text escape stays for "not listed".
        <div className="decision-choices" data-testid="decision-choices">
          <div className="decision-choice-row">
            {item.options.map((o, i) => (
              <button key={i} className="decision-choice" disabled={busy}
                      data-testid="decision-choice" onClick={() => submit(o.value)}>
                {o.label || o.value}
              </button>
            ))}
          </div>
          <div className="decision-answer">
            <input className="decision-input" value={value} placeholder="Somewhere else — type it"
                   data-testid="decision-input" onChange={(e) => setValue(e.target.value)} disabled={busy} />
            <button className="decision-send" data-testid="decision-send"
                    onClick={() => submit()} disabled={busy || !value.trim()}>Send</button>
          </div>
        </div>
      ) : (
        <div className="decision-answer">
          <input className="decision-input" value={value} placeholder="Type what it should be"
                 data-testid="decision-input" onChange={(e) => setValue(e.target.value)} disabled={busy} />
          <button className="decision-send" data-testid="decision-send"
                  onClick={() => submit()} disabled={busy || !value.trim()}>Send</button>
        </div>
      )}
      {err && <p className="decision-err" data-testid={"decision-err-" + item.id}>{err}</p>}
    </div>
  );
}

export function DecisionQueue({ decisions, onChanged, buildings, onAddBuilding }) {
  return (
    <div className="decision-list" data-testid="decision-list">
      {decisions.items.map((item) => (
        <DecisionCard key={item.id} item={item} onDone={onChanged}
                      buildings={buildings} onAddBuilding={onAddBuilding} />
      ))}
    </div>
  );
}
